/*
 * textScoring — heuristic ranking for the "Best fits" section in TextsTab.
 *
 * When the user has a text node selected in the canvas, we want to surface
 * the chips that look like good replacements without making them search.
 * "Good" here means "the same shape of copy", not "literally similar words":
 *
 *   - length ratio (character count)
 *   - word-count ratio
 *   - same inferred type bucket (cta / heading / body)
 *   - same capitalization pattern (UPPER / Title / Sentence / lower)
 *   - both end with terminal punctuation, or neither do
 *
 * Token overlap is intentionally NOT a signal — copying with the same words
 * usually isn't what the user wants when picking a "replacement".
 */

export function inferTextType(text) {
  const t = (text || '').trim();
  if (!t) return '';
  if (t.length <= 30 && !/[.!?]$/.test(t)) return 'cta';
  if (t.length <= 80 && !/[.!?]$/.test(t)) return 'heading';
  return 'body';
}

function capPattern(text) {
  const t = (text || '').trim();
  if (!t) return '';
  if (t === t.toUpperCase() && /[A-Z]/.test(t)) return 'UPPER';
  if (t === t.toLowerCase()) return 'lower';
  const words = t.split(/\s+/);
  if (words.length > 1 && words.every((w) => /^[A-Z]/.test(w))) return 'Title';
  return 'Sentence';
}

function ratio(a, b) {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return hi === 0 ? 0 : lo / hi;
}

export function scoreTextMatch(candidate, target) {
  if (!candidate || !target) return 0;
  // Identical content is useless as a "fit" — nothing to replace.
  if (candidate === target) return 0;

  const lenScore = ratio(candidate.length, target.length) * 40;
  const wordScore = ratio(
    candidate.trim().split(/\s+/).length,
    target.trim().split(/\s+/).length
  ) * 20;
  const typeScore = inferTextType(candidate) === inferTextType(target) ? 25 : 0;
  const capScore = capPattern(candidate) === capPattern(target) ? 10 : 0;
  const punctC = /[.!?]$/.test(candidate.trim());
  const punctT = /[.!?]$/.test(target.trim());
  const punctScore = punctC === punctT ? 5 : 0;

  return lenScore + wordScore + typeScore + capScore + punctScore;
}

/**
 * Rank chips by similarity to the target text. De-dupes by chip text so the
 * same string scraped from multiple categories doesn't take all 4 slots.
 *
 * @param {string} target — the currently-selected text
 * @param {Array<{id:string, text:string, category:string}>} chips
 * @param {object} [opts]
 * @param {number} [opts.limit=4]
 * @param {number} [opts.minScore=55] — drop matches below this threshold
 * @returns {Array<{id:string, text:string, category:string, score:number}>}
 */
export function bestFitsFor(target, chips, opts = {}) {
  const { limit = 4, minScore = 55 } = opts;
  if (!target || !Array.isArray(chips) || chips.length === 0) return [];
  const seen = new Set();
  const scored = [];
  for (const c of chips) {
    if (seen.has(c.text)) continue;
    const score = scoreTextMatch(c.text, target);
    if (score < minScore) continue;
    seen.add(c.text);
    scored.push({ ...c, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}
